import { ComponentType, FC, ReactNode, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  FormField,
  FormFieldDescription,
  FormFieldError,
  FormFieldLabel,
} from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { LucideProps } from "lucide-react";

interface Props extends Omit<React.ComponentProps<typeof Input>, "id"> {
  label: ReactNode;
  error?: ReactNode;
  id: string;
  icon?: ComponentType<LucideProps>;
  helperText?: ReactNode;
  togglePassword?: boolean;
}

export const CommonInput: FC<Props> = ({
  label,
  placeholder = "",
  required = false,
  error,
  id,
  value,
  onChange,
  onBlur,
  type = "text",
  autoComplete,
  className,
  disabled,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  icon: Icon,
  helperText,
  togglePassword = false,
  ...inputProps
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const showToggle = togglePassword && type === "password";
  const inputType = showToggle && isPasswordVisible ? "text" : type;
  const descriptionId = helperText ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [ariaDescribedBy, descriptionId, errorId]
    .filter(Boolean)
    .join(" ");

  return (
    <FormField>
      <FormFieldLabel htmlFor={id} required={required}>
        {Icon && <Icon className="text-muted-foreground h-4 w-4" />}
        <span>{label}</span>
      </FormFieldLabel>
      <div className="relative">
        <Input
          {...inputProps}
          id={id}
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : ariaInvalid}
          className={cn(showToggle && "pr-10", className)}
        />
        {showToggle && (
          <button
            type="button"
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            aria-label={
              isPasswordVisible ? "パスワードを非表示" : "パスワードを表示"
            }
            aria-pressed={isPasswordVisible}
            aria-controls={id}
            disabled={disabled}
            tabIndex={-1}
            className="text-muted-foreground hover:text-foreground focus-visible:text-foreground absolute right-3 top-1/2 -translate-y-1/2 transition-colors focus:outline-none"
          >
            {isPasswordVisible ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
      {helperText && (
        <FormFieldDescription id={descriptionId}>
          {helperText}
        </FormFieldDescription>
      )}
      <FormFieldError id={errorId}>{error}</FormFieldError>
    </FormField>
  );
};
